/* Polaroid variant picker - companion script
   --------------------------------------------------------------------
   Load this file GLOBALLY, e.g. in layout/theme.liquid:

     <script src="{{ 'polaroid-variant-picker.js' | asset_url }}" defer="defer"></script>

   Why global: themes inject product HTML client-side (quick-add
   drawers, quick view modals) via innerHTML, and browsers never execute
   <script> tags inserted that way. A custom element that is already
   defined, however, upgrades automatically the moment its tag is
   injected - so defining it once per page makes the picker work both on
   server-rendered product pages and inside injected drawers. */

if (!customElements.get('polaroid-variant-picker')) {
  const NATIVE_HOST_SELECTOR =
    'variant-options, variant-selects, variant-radios, variant-picker';

  customElements.define(
    'polaroid-variant-picker',
    class PolaroidVariantPicker extends HTMLElement {
      connectedCallback() {
        if (this._onChange) return;

        this._onChange = (event) => {
          const input = event.target.closest('.pvp__input');
          if (input) this.syncNativePicker(input);
        };
        this.addEventListener('change', this._onChange);
        this.hideNativePicker();

        /* Preselected default (no ?variant= in the URL): nudge the
           theme's hidden picker once so price, gallery and cart match
           the checked card. Deferred so surrounding code finishes
           first - e.g. the quick-add drawer calls
           variant-options.initialize() right after injecting HTML. */
        if (this.dataset.autoSelect) {
          const run = () => {
            const id = CSS.escape(this.dataset.autoSelect);
            const input = this.querySelector('.pvp__input[value="' + id + '"]');
            if (input) this.syncNativePicker(input);
          };
          const schedule = () => setTimeout(run, 0);
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', schedule, { once: true });
          } else {
            schedule();
          }
        }
      }

      disconnectedCallback() {
        if (this._onChange) {
          this.removeEventListener('change', this._onChange);
          this._onChange = null;
        }
        this.showNativePicker();
      }

      isForeign(el) {
        return !el.closest('polaroid-variant-picker');
      }

      hideSelector() {
        return this.dataset.hideSelector || NATIVE_HOST_SELECTOR + ', .wt-product__options';
      }

      /* True when root contains the theme's own option controls, not
         collection filters or other radios elsewhere on the page. */
      hasOptionControls(root) {
        for (const host of root.querySelectorAll(NATIVE_HOST_SELECTOR)) {
          if (this.isForeign(host)) return true;
        }
        for (const el of root.querySelectorAll('select:not([name="id"])')) {
          if (!this.isForeign(el)) continue;
          if (
            el.closest(
              NATIVE_HOST_SELECTOR + ', product-form, .product-form__input, form[action*="/cart/add"]'
            )
          ) {
            return true;
          }
        }
        return false;
      }

      /* Smallest ancestor that contains the theme's own option controls.
         Stops at the Shopify section so collection filters / other
         products on the page are never treated as this picker's target. */
      optionScope() {
        let node = this.parentElement;
        while (node && node !== document.body) {
          if (this.hasOptionControls(node)) return node;
          if (node.classList.contains('shopify-section')) break;
          node = node.parentElement;
        }
        return null;
      }

      /* Prefer the native picker sitting next to these cards (the block
         this UI replaces). Falling back to the first host in scope would
         hide complementary / quick-add pickers in the same product column. */
      findNativePickerHost() {
        const selector = this.hideSelector();
        const searchRoot = (root) => {
          if (!root || !root.querySelector) return null;
          try {
            if (root.matches && root.matches(selector)) return root;
            return root.querySelector(selector);
          } catch (error) {
            return null;
          }
        };

        let node = this;
        while (node && node !== document.body) {
          for (const dir of ['previousElementSibling', 'nextElementSibling']) {
            let sib = node[dir];
            while (sib) {
              const found = searchRoot(sib);
              if (found && this.isForeign(found)) return found;
              sib = sib[dir];
            }
          }
          if (node.classList.contains('shopify-section')) break;
          node = node.parentElement;
        }

        const scope = this.optionScope();
        if (!scope) return null;
        try {
          return [...scope.querySelectorAll(selector)].find((el) => this.isForeign(el)) || null;
        } catch (error) {
          return null;
        }
      }

      hideNativePicker() {
        if (!this.hasAttribute('data-hide-native')) return;
        const host = this.findNativePickerHost();
        if (!host) return;
        const wrap = host.closest('.wt-product__options');
        const target = wrap && !wrap.contains(this) ? wrap : host;
        target.classList.add('pvp-native-hidden');
        this._hiddenNative = target;
      }

      showNativePicker() {
        if (!this._hiddenNative) return;
        this._hiddenNative.classList.remove('pvp-native-hidden');
        this._hiddenNative = null;
      }

      /* Smallest ancestor containing a variant id field, used only when
         no option-level controls exist near the picker. */
      variantIdScope() {
        let node = this.parentElement;
        while (node && node !== document.body) {
          const fields = node.querySelectorAll('select[name="id"], input[name="id"]');
          for (const el of fields) {
            if (this.isForeign(el)) return node;
          }
          if (node.classList.contains('shopify-section')) break;
          node = node.parentElement;
        }
        return null;
      }

      syncNativePicker(input) {
        let values = [];
        try {
          values = JSON.parse(input.dataset.optionValues || '[]');
        } catch (error) {
          console.warn('polaroid-variant-picker: invalid data-option-values', error);
        }

        const host = this.findNativePickerHost();
        const scope = host || this.optionScope();
        const drovePicker = scope ? this.driveOptionControls(scope, values) : false;

        if (!drovePicker) {
          const idScope = this.variantIdScope();
          const fallback = idScope ? this.applyVariantId(idScope, input.value) : false;

          /* Only warn when nothing could be updated. A hidden
             <input name="id"> is a valid fallback for vintage forms. */
          if (!fallback) {
            console.warn(
              'polaroid-variant-picker: no native variant picker found near the cards. ' +
                "Keep the theme's variant picker block enabled (the snippet hides it " +
                'with CSS) so price and gallery update correctly.'
            );
          }
        }
      }

      /* Set every option to the chosen variant, then fire a single
         change event. Sequential label clicks can open dropdown drawers,
         abort in-flight fetches, or skip disabled radios. */
      driveOptionControls(scope, values) {
        if (!values.length) return false;

        const radios = values.map((value, index) => this.findOptionRadio(scope, value, index));
        if (radios.every(Boolean)) {
          let changed = false;
          radios.forEach((radio) => {
            if (!radio.checked) {
              radio.checked = true;
              changed = true;
            }
          });
          if (changed) this.dispatchNativeChange(radios[radios.length - 1]);
          return true;
        }

        const selects = values.map((value, index) => this.findOptionSelect(scope, value, index));
        if (selects.every(Boolean)) {
          let changed = false;
          selects.forEach((select, index) => {
            if (select.value !== values[index]) {
              select.value = values[index];
              changed = true;
            }
          });
          if (changed) {
            const last = selects[selects.length - 1];
            last.dispatchEvent(new Event('input', { bubbles: true }));
            last.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return true;
        }

        return false;
      }

      dispatchNativeChange(fromEl) {
        const host = fromEl.closest(NATIVE_HOST_SELECTOR) || fromEl;
        host.dispatchEvent(new Event('change', { bubbles: true }));
      }

      findOptionRadio(scope, value, index) {
        const radios = [...scope.querySelectorAll('input[type="radio"]')].filter((el) =>
          this.isForeign(el)
        );
        const position = String(index + 1);
        const byPosition = radios.find(
          (el) => el.dataset.position === position && el.value === value
        );
        if (byPosition) return byPosition;

        const groups = [];
        const seen = new Map();
        for (const el of radios) {
          const key = el.name || '_';
          if (!seen.has(key)) {
            seen.set(key, []);
            groups.push(seen.get(key));
          }
          seen.get(key).push(el);
        }
        const group = groups[index];
        if (group) {
          const match = group.find((el) => el.value === value);
          if (match) return match;
        }

        return null;
      }

      findOptionSelect(scope, value, index) {
        const selects = [...scope.querySelectorAll('select:not([name="id"])')].filter((el) =>
          this.isForeign(el)
        );
        const byIndex = selects[index];
        if (byIndex && [...byIndex.options].some((option) => option.value === value)) {
          return byIndex;
        }
        return null;
      }

      /* Fallbacks for contexts without option-level controls: a master
         <select name="id"> (vintage themes), or the hidden variant id
         input in the product form. */
      applyVariantId(scope, variantId) {
        const master = [...scope.querySelectorAll('select[name="id"]')].find((el) =>
          [...el.options].some((option) => option.value === variantId)
        );
        if (master) {
          master.value = variantId;
          master.dispatchEvent(new Event('change', { bubbles: true }));
          return 'select';
        }

        const hidden = [...scope.querySelectorAll('input[name="id"]')].find((el) =>
          this.isForeign(el)
        );
        if (hidden) {
          hidden.value = variantId;
          hidden.dispatchEvent(new Event('change', { bubbles: true }));
          this.maybeUpdateUrl(variantId);
          return 'input';
        }

        return false;
      }

      maybeUpdateUrl(variantId) {
        const productUrl = (this.dataset.productUrl || '').split('?')[0].replace(/\/$/, '');
        if (!productUrl) return;
        const path = window.location.pathname.replace(/\/$/, '');
        if (path !== productUrl && !path.endsWith(productUrl)) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url);
      }
    }
  );
}
